import React, { useState, useEffect } from "react";

import PageLayout from "../components/PageLayout.jsx";
import { useApi } from "../services/ApiService";
import ExportCsvComponent from "../components/ExportCsv.jsx";
import clsx from "clsx";

const sortObjectsByKey = (key) => (a, b) => {
  a = typeof a[key] === "string" ? a[key].toLowerCase() : a[key];
  b = typeof b[key] === "string" ? b[key].toLowerCase() : b[key];
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
};

export default function ManageSite() {
  const api = useApi();
  const [paginationToken, setPaginationToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [error, setError] = useState(null);

  const fetchUsers = async (signal) => {
    if (loading) return;
    setLoading(true);
    try {
      const params = paginationToken ? { token: paginationToken, limit: 50 } : { limit: 50 };
      const response = await api.usersGet(params, { signal });
      if (response.success) {
        setError(null);
        const { data } = response;
        if (data?.users?.length > 0) {
          // process loaded users
          const sortedUsers = [...users, ...data.users].sort(sortObjectsByKey("name"));
          setUsers(sortedUsers);
        }
        setPaginationToken(data?.token || null);
      }

      const adminResponse = await api.usersGet({ groupName: "admin-users" }, { signal });
      setAdmins(adminResponse.data?.users || []);
    } catch (error) {
      console.error("Error fetching users:", error);
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    fetchUsers(controller.signal);
    return () => controller.abort();
  }, []); // eslint-disable-line

  const count = {
    users: users.length,
    terms: users.filter((u) => u?.custom?.terms === "true").length,
    confirmed: users.filter((u) => u?.userStatus === "CONFIRMED").length,
  };

  return (
    <PageLayout title="Admin Portal">
      <section aria-labelledby="section-heading rounded-lg">
        <div className="bg-opacity-50 relative rounded-lg bg-gray-300 p-4 dark:bg-gray-600">
          <div className="sm:flex sm:items-center">
            <div className="sm:flex-auto">
              <h2>
                Users
                {loading && (
                  <span className="relative ml-2 inline-flex h-3 w-3">
                    <span className="bg-proof-blue absolute inline-flex h-full w-full animate-ping rounded-full opacity-75"></span>
                    <span className="bg-proof-blue relative inline-flex h-3 w-3 rounded-full"></span>
                  </span>
                )}
                {paginationToken && (
                  <a
                    href="#"
                    onClick={() => fetchUsers(null)}
                    className="text-proof-purple dark:text-proof-blue ml-2 text-sm hover:underline"
                  >
                    load more
                  </a>
                )}
              </h2>
              <p className="mt-2">A list of all the site users including their name, email, role and status.</p>
            </div>
            <div className="mt-4 flex flex-col items-end justify-center sm:mt-0 sm:ml-16 sm:flex-none">
              <ExportCsvComponent data={users} />
            </div>
          </div>
          <div className="mt-2 flow-root">
            <div className="-mx-4 -my-2 overflow-x-auto lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle lg:px-8">
                <table className="min-w-full divide-y divide-gray-600 dark:divide-gray-300">
                  <colgroup>
                    <col className="sm:w-3/12" />
                    <col className="sm:w-4/12" />
                    <col className="sm:w-2/12" />
                    <col className="sm:w-1/12" />
                  </colgroup>
                  <thead className="bg-gray-400 dark:bg-gray-800">
                    <tr>
                      <th scope="col" className="py-3.5 pr-3 pl-4 text-left text-sm font-semibold sm:pl-4">
                        Name
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold">
                        Email
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold">
                        Terms ({count.terms})
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold">
                        Account ({count.confirmed}/{count.users})
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-400 dark:divide-gray-200">
                    {loading && users.length === 0 && (
                      <tr>
                        <td
                          colSpan={5}
                          className="py-4 pr-3 pl-4 text-center text-sm font-medium whitespace-nowrap sm:pl-4"
                        >
                          Loading... please wait
                        </td>
                      </tr>
                    )}
                    {error && (
                      <tr>
                        <td
                          colSpan={5}
                          className="py-4 pr-3 pl-4 text-center text-sm font-medium whitespace-nowrap sm:pl-4"
                        >
                          Error loading users: {`${error}`}
                        </td>
                      </tr>
                    )}
                    {users.map((user) => {
                      const isAdmin = admins.find((a) => a.sub === user.sub);
                      return (
                        <tr key={user.sub}>
                          <td
                            className={clsx(
                              "py-4 pr-3 pl-4 text-sm font-medium whitespace-nowrap sm:pl-4",
                              isAdmin && "text-proof-yellow",
                            )}
                          >
                            {user.name}
                          </td>
                          <td className="px-3 py-4 text-sm whitespace-nowrap">
                            {user.email} ({user.username})
                          </td>
                          <td className="px-3 py-4 text-sm whitespace-nowrap">
                            {user?.custom?.terms === "true" ? "Yes" : "No"}
                          </td>
                          <td className="px-3 py-4 text-sm whitespace-nowrap">{user.userStatus}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
